/**
 * Studio Zustand store.
 *
 * Holds:
 *   - entity caches (universes / projects / blocks) so the calendar view can
 *     render without thrashing on the network
 *   - the active anchors (universe / project / block) for the chat context
 *   - the chat session id + turn history + in-flight streaming state
 *   - calendar view / anchor date for the calendar grid
 *
 * User-isolation: same pattern as useZinsserStore — clear persisted state on
 * the first effect that detects a user-id switch in localStorage. Sensitive
 * fields (turns) are not persisted; entity caches are kept for snappy reload.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  listUniverses, listProjects, listBlocks, listSessions, getSession,
  createSession as apiCreateSession, deleteSession as apiDeleteSession,
  streamStudio,
  type ListBlocksParams,
  type StudioBlock, type StudioProject, type StudioUniverse,
  type StudioSessionSummary, type StudioSessionTurn, type StudioToolCall, type StudioToolResult,
} from '@/lib/studio'
import type { CalendarView } from '@/features/studio/lib/time'
import { rangeForView } from '@/features/studio/lib/time'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  thinking?: string
  tool_calls?: { name: string; ok: boolean; summary: string }[]
  timestamp: number
}

interface StudioState {
  // Hydration / loading
  initialized: boolean
  isLoading: boolean
  /** Last fatal bootstrap error — surfaced as a banner so silent failures
   *  (expired auth, backend unreachable) don't read as "the app cleared my data." */
  lastError: string | null

  // Entity caches
  universes: StudioUniverse[]
  projects: StudioProject[]
  blocks: StudioBlock[]

  // Active anchors (drive chat context + filters)
  activeUniverseId: string | null
  activeProjectId: string | null
  activeBlockId: string | null

  // Calendar
  calendarView: CalendarView
  calendarAnchor: number  // epoch ms — Date isn't JSON-serializable; store as number

  // Sessions
  sessions: StudioSessionSummary[]
  activeSessionId: string | null
  messages: ChatMessage[]

  // Streaming state
  isStreaming: boolean
  streamingThinking: string
  streamingToolCalls: { name: string; ok: boolean | null; summary: string }[]

  // Actions — bootstrap & cache loaders
  bootstrap: () => Promise<void>
  refreshBlocks: (filters?: ListBlocksParams) => Promise<void>
  refreshAll: () => Promise<void>

  // Anchors
  setActiveUniverse: (id: string | null) => void
  setActiveProject: (id: string | null) => void
  setActiveBlock: (id: string | null) => void

  // Reschedule (drag-and-drop) — optimistic update then PATCH /studio/blocks/:id
  rescheduleBlock: (blockId: string, startsAt: Date, endsAt: Date) => Promise<void>

  // Calendar
  setCalendarView: (v: CalendarView) => void
  setCalendarAnchor: (d: Date) => void
  shiftCalendar: (deltaDays: number) => void
  goToToday: () => void

  // Sessions
  refreshSessions: () => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  newSession: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>

  // Chat
  sendChat: (content: string) => Promise<void>

  // User-isolation helper
  resetForUserSwitch: () => void
}

const initialState = {
  initialized: false,
  isLoading: false,
  lastError: null,
  universes: [],
  projects: [],
  blocks: [],
  activeUniverseId: null,
  activeProjectId: null,
  activeBlockId: null,
  calendarView: 'week' as CalendarView,
  calendarAnchor: Date.now(),
  sessions: [],
  activeSessionId: null,
  messages: [],
  isStreaming: false,
  streamingThinking: '',
  streamingToolCalls: [],
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export const useStudioStore = create<StudioState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ─── Bootstrap & caches ─────────────────────────────────────────

      bootstrap: async () => {
        if (get().initialized) return
        set({ isLoading: true, lastError: null })
        try {
          const [universes, projects] = await Promise.all([
            listUniverses(),
            listProjects(),
          ])
          set({ universes, projects, initialized: true })

          // Respect persisted activeUniverseId; only default if nothing set.
          if (!get().activeUniverseId && universes.length) {
            set({ activeUniverseId: universes[0].id })
          }

          // Load blocks for the persisted (or default) calendar window.
          await get().refreshBlocks()

          // Load the sessions list — but don't let refreshSessions auto-load
          // a different session than the one we just rehydrated from
          // localStorage. We handle resume here explicitly.
          try {
            const sessions = await listSessions('active', 50)
            set({ sessions })
          } catch (e) {
            console.error('[studio] sessions load failed', e)
          }

          // Resume previous session if we have one persisted; else pick the
          // most recent. Treat a missing/deleted session as "fall back to
          // most recent" rather than blowing up.
          const { activeSessionId, sessions } = get()
          if (activeSessionId) {
            try {
              await get().loadSession(activeSessionId)
            } catch {
              if (sessions.length) {
                await get().loadSession(sessions[0].session_id)
              } else {
                set({ activeSessionId: null, messages: [] })
              }
            }
          } else if (sessions.length) {
            await get().loadSession(sessions[0].session_id)
          }
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          console.error('[studio] bootstrap failed', e)
          set({ lastError: msg })
        } finally {
          set({ isLoading: false })
        }
      },

      refreshBlocks: async (filters) => {
        const { calendarAnchor, calendarView } = get()
        const { starts, ends } = rangeForView(new Date(calendarAnchor), calendarView)
        try {
          const blocks = await listBlocks({
            starts_after: starts.toISOString(),
            starts_before: ends.toISOString(),
            include_archived: false,
            order_by: 'starts_at',
            order_dir: 'asc',
            limit: 500,
            ...filters,
          })
          set({ blocks })
        } catch (e) {
          console.error('[studio] refreshBlocks failed', e)
        }
      },

      refreshAll: async () => {
        try {
          const [universes, projects] = await Promise.all([
            listUniverses(),
            listProjects(),
          ])
          set({ universes, projects })
          await get().refreshBlocks()
        } catch (e) {
          console.error('[studio] refreshAll failed', e)
        }
      },

      // ─── Anchors ────────────────────────────────────────────────────

      setActiveUniverse: (id) =>
        set({ activeUniverseId: id, activeProjectId: null, activeBlockId: null }),

      setActiveProject: (id) =>
        set({ activeProjectId: id, activeBlockId: null }),

      setActiveBlock: (id) => set({ activeBlockId: id }),

      // Reschedule via drag. Optimistic: the calendar updates instantly so
      // the tile "sticks" where the user dropped it; if the server rejects
      // we revert and surface the error. Server already accepts both
      // starts_at and ends_at via the entity service, so we send both rather
      // than counting on duration_minutes derivation.
      rescheduleBlock: async (blockId, startsAt, endsAt) => {
        const prev = get().blocks
        const target = prev.find((b) => b.id === blockId)
        if (!target) return

        const newDuration = Math.max(
          1,
          Math.round((endsAt.getTime() - startsAt.getTime()) / 60000),
        )
        const optimistic = prev.map((b) =>
          b.id === blockId
            ? {
                ...b,
                starts_at: startsAt.toISOString(),
                ends_at: endsAt.toISOString(),
                duration_minutes: newDuration,
              }
            : b,
        )
        set({ blocks: optimistic })

        try {
          // Lazy import to avoid pulling the api module into the store's
          // top-level dependency graph (keeps the persist hydration light).
          const { updateBlock } = await import('@/lib/studio')
          await updateBlock(blockId, {
            starts_at: startsAt.toISOString(),
            ends_at: endsAt.toISOString(),
            duration_minutes: newDuration,
          })
        } catch (e) {
          console.error('[studio] rescheduleBlock failed; reverting', e)
          set({ blocks: prev, lastError: e instanceof Error ? e.message : String(e) })
          // Pull fresh server state so we're not stuck on a stale revert.
          void get().refreshBlocks()
        }
      },

      // ─── Calendar nav ───────────────────────────────────────────────

      setCalendarView: (v) => {
        set({ calendarView: v })
        // Refresh blocks for the new window
        void get().refreshBlocks()
      },

      setCalendarAnchor: (d) => {
        set({ calendarAnchor: d.getTime() })
        void get().refreshBlocks()
      },

      shiftCalendar: (deltaDays) => {
        const next = new Date(get().calendarAnchor)
        const step = get().calendarView === 'day' ? deltaDays : deltaDays * 7
        next.setDate(next.getDate() + step)
        get().setCalendarAnchor(next)
      },

      goToToday: () => get().setCalendarAnchor(new Date()),

      // ─── Sessions ───────────────────────────────────────────────────

      refreshSessions: async () => {
        // Re-fetch the sessions list but DON'T auto-switch sessions here.
        // Bootstrap handles the initial resume; subsequent refreshes (e.g.
        // after a chat turn) should leave the user where they are.
        try {
          const sessions = await listSessions('active', 50)
          set({ sessions })
        } catch (e) {
          console.error('[studio] refreshSessions failed', e)
        }
      },

      loadSession: async (sessionId) => {
        try {
          const detail = await getSession(sessionId, 100)
          const messages: ChatMessage[] = detail.turns.map((t: StudioSessionTurn) => ({
            id: t.turn_id,
            role: t.role,
            content: t.content,
            thinking: t.thinking || undefined,
            timestamp: t.timestamp ? Date.parse(t.timestamp) : Date.now(),
          }))
          set({ activeSessionId: sessionId, messages })
        } catch (e) {
          console.error('[studio] loadSession failed', e)
        }
      },

      newSession: async () => {
        try {
          const s = await apiCreateSession()
          set((prev) => ({
            sessions: [s, ...prev.sessions],
            activeSessionId: s.session_id,
            messages: [],
          }))
        } catch (e) {
          console.error('[studio] newSession failed', e)
        }
      },

      deleteSession: async (sessionId) => {
        try {
          await apiDeleteSession(sessionId)
          set((prev) => ({
            sessions: prev.sessions.filter((s) => s.session_id !== sessionId),
            activeSessionId:
              prev.activeSessionId === sessionId ? null : prev.activeSessionId,
            messages: prev.activeSessionId === sessionId ? [] : prev.messages,
          }))
        } catch (e) {
          console.error('[studio] deleteSession failed', e)
        }
      },

      // ─── Chat ───────────────────────────────────────────────────────

      sendChat: async (content) => {
        const text = content.trim()
        if (!text || get().isStreaming) return

        // Optimistic user message
        const userMsg: ChatMessage = {
          id: newId(),
          role: 'user',
          content: text,
          timestamp: Date.now(),
        }
        set((prev) => ({
          messages: [...prev.messages, userMsg],
          isStreaming: true,
          streamingThinking: '',
          streamingToolCalls: [],
        }))

        const {
          activeSessionId, activeBlockId, activeProjectId, activeUniverseId,
        } = get()

        let assistantMessage = ''
        const toolCallSummaries: { name: string; ok: boolean; summary: string }[] = []
        let assistantThinking = ''

        try {
          await streamStudio(
            {
              content: text,
              session_id: activeSessionId || undefined,
              block_id: activeBlockId || undefined,
              project_id: activeProjectId || undefined,
              universe_id: activeUniverseId || undefined,
            },
            {
              onThinkingStart: () => {
                set({ streamingThinking: '' })
              },
              onThinkingDelta: (d) => {
                assistantThinking += d
                set({ streamingThinking: assistantThinking })
              },
              onThinkingComplete: () => {
                set({ streamingThinking: assistantThinking })
              },
              onToolCall: (call: StudioToolCall) => {
                set((prev) => ({
                  streamingToolCalls: [
                    ...prev.streamingToolCalls,
                    { name: call.name, ok: null, summary: 'running…' },
                  ],
                }))
              },
              onToolResult: (r: StudioToolResult) => {
                toolCallSummaries.push({ name: r.name, ok: r.ok, summary: r.summary })
                set((prev) => {
                  // Find the most recent matching tool_call entry without a resolution
                  const next = [...prev.streamingToolCalls]
                  for (let i = next.length - 1; i >= 0; i--) {
                    if (next[i].name === r.name && next[i].ok === null) {
                      next[i] = { name: r.name, ok: r.ok, summary: r.summary }
                      break
                    }
                  }
                  return { streamingToolCalls: next }
                })
              },
              onSessionComplete: (payload) => {
                assistantMessage = payload.response
                // First-turn auto-mint: the server may issue a session_id
                if (!activeSessionId) {
                  set({ activeSessionId: payload.session_id })
                }
              },
              onDone: () => {
                const finalMsg: ChatMessage = {
                  id: newId(),
                  role: 'assistant',
                  content: assistantMessage || '(no reply)',
                  thinking: assistantThinking || undefined,
                  tool_calls: toolCallSummaries.length ? toolCallSummaries : undefined,
                  timestamp: Date.now(),
                }
                set((prev) => ({
                  messages: [...prev.messages, finalMsg],
                  isStreaming: false,
                  streamingThinking: '',
                  streamingToolCalls: [],
                }))
                // If the agent did any writes, refresh blocks so the calendar updates
                if (toolCallSummaries.some((tc) => tc.ok)) {
                  void get().refreshAll()
                }
                void get().refreshSessions()
              },
              onError: (err) => {
                console.error('[studio] stream error', err)
                const errMsg: ChatMessage = {
                  id: newId(),
                  role: 'assistant',
                  content: `Error: ${err}`,
                  timestamp: Date.now(),
                }
                set((prev) => ({
                  messages: [...prev.messages, errMsg],
                  isStreaming: false,
                  streamingThinking: '',
                  streamingToolCalls: [],
                }))
              },
            },
          )
        } catch (e) {
          console.error('[studio] sendChat unexpected error', e)
          set({ isStreaming: false, streamingThinking: '', streamingToolCalls: [] })
        }
      },

      // ─── User isolation reset ───────────────────────────────────────

      resetForUserSwitch: () => set({ ...initialState, calendarAnchor: Date.now() }),
    }),
    {
      name: 'studio-store-v1',
      // Persist only stable preferences and resume pointers. Entity caches
      // reload on bootstrap; turns / streaming state should never persist
      // (encryption is at the server layer; localStorage is plaintext).
      partialize: (s) => ({
        calendarView: s.calendarView,
        calendarAnchor: s.calendarAnchor,
        activeUniverseId: s.activeUniverseId,
        activeProjectId: s.activeProjectId,
        // Resume the same chat session across refreshes. Without this the
        // user lands in whichever session was most-recently-modified, which
        // can feel like "all my data is gone" when in fact the chat is
        // just pointing at a different session row.
        activeSessionId: s.activeSessionId,
      }),
    },
  ),
)
